import { STANDARD_COLORS, StandardColorTheme, WidgetColors } from "./widget-colors"
import { createContext, useContext, useMemo } from "react"

export type WidgetCssVars = Partial<WidgetColors> & Partial<Record<string, string>>

type UseWidgetStyling = {
  theme: StandardColorTheme
  custom?: WidgetCssVars
}

const WidgetStylingContext = createContext<UseWidgetStyling>({
  theme: "light"
})

export function WidgetStylingProvider(props: {
  value: UseWidgetStyling,
  children?: React.ReactNode
}) {
  const stableValue = useMemo<UseWidgetStyling>(() => ({
    theme: props.value.theme,
    custom: props.value.custom
  }), [
    JSON.stringify(props.value) // should be fine
  ])
  return (
    <WidgetStylingContext.Provider value={stableValue}>
      {props.children}
    </WidgetStylingContext.Provider>
  )
}

export const useWidgetStyling = () => useContext(WidgetStylingContext)

export function useWidgetColorsCssVars(defaults?: WidgetCssVars) {
  const provided = useWidgetStyling()
  const styles = getDerivedStyles({
    ...STANDARD_COLORS[provided.theme],
    ...defaults
  }, provided.custom)

  const result: Record<string, string> = {}

  for (const key of Object.keys(styles)) {
    const cssKey = `--w-${camelToKebab(key)}`
    result[cssKey] = styles[key]!
  }

  return result as React.CSSProperties
}

function getDerivedStyles(
  defaults?: WidgetCssVars,
  custom?: WidgetCssVars
): WidgetCssVars {
  const result: WidgetCssVars = {}

  const keys = new Set([
    ...Object.keys(defaults ?? {}),
    ...Object.keys(custom ?? {})
  ])

  for (const key of keys) {
    const customValue = custom?.[key]
    const defaultValue = defaults?.[key]

    if (customValue === "" || customValue === null || customValue === undefined) {
      if (defaultValue !== undefined) {
        result[key] = defaultValue
      }
    } else {
      result[key] = customValue
    }
  }

  return result
}

function camelToKebab(str: string) {
  return str.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)
}
